import { Avatar, Box, Flex, IconButton, Link, Text, Tooltip } from '@lego/klik-ui';
import { ExternalLink } from '@lego/klik-ui/icons';
import { Navigate, useParams } from 'react-router-dom';
import { BreadCrumb } from '@/components/common/BreadCrumb';
import { CopyableText } from '@/components/common/CopyableText';
import { Loader } from '@/components/common/Loader';
import { useDocumentTitle } from '@/hooks/common';
import { useGraphEnabled, useGraphUser, useGraphUserPhoto } from '@/hooks/graph';
import { useRegisterPageContext } from '@/providers/PageContextProvider';
import { paths } from '@/utility/global_paths';

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Flex borderBottom="1px solid" borderColor="slate.100" gap={4} paddingY={3}>
    <Text color="gray.600" fontWeight="medium" minWidth="200px">
      {label}
    </Text>
    <Text>{value || 'N/A'}</Text>
  </Flex>
);

const entraUserUrl = (userId: string) =>
  `https://entra.microsoft.com/#view/Microsoft_AAD_UsersAndTenants/UserProfileMenuBlade/~/overview/userId/${encodeURIComponent(userId)}/hidePreviewBanner~/true`;

const OidValue = ({ oid }: { oid: string }) => (
  <Flex alignItems="center" gap={2}>
    <CopyableText
      copyLabel="Copy OID"
      textProps={{ fontFamily: 'mono', fontSize: 'sm' }}
      value={oid}
    />
    <Tooltip label="View in Entra ID">
      <IconButton
        aria-label="View in Entra ID"
        as="a"
        href={entraUserUrl(oid)}
        icon={<ExternalLink />}
        rel="noopener noreferrer"
        size="xs"
        target="_blank"
        variant="ghost"
      />
    </Tooltip>
  </Flex>
);

const EmailValue = ({ email }: { email: string | null | undefined }) =>
  email ? (
    <Link color="light-blue.600" href={`mailto:${email}`}>
      {email}
    </Link>
  ) : (
    <>N/A</>
  );

export const UserProfilePage = () => {
  const { oid } = useParams();
  const graphEnabled = useGraphEnabled(oid);
  const { data: user, error, isLoading } = useGraphUser(oid);
  const { data: photoUrl } = useGraphUserPhoto(oid);
  useRegisterPageContext(user);
  useDocumentTitle(user?.displayName ? `User - ${user.displayName}` : `User - ${oid ?? ''}`);

  if (!oid) {
    return <Navigate to={paths.notFound} />;
  }

  if (graphEnabled && isLoading) {
    return (
      <Box>
        <Loader />
      </Box>
    );
  }

  return (
    <Box>
      <BreadCrumb
        crumbs={[
          { label: 'Home', path: paths.home },
          { label: user?.displayName ?? oid, path: paths.userByOid(oid) },
        ]}
      />
      <Box background="white" borderRadius="lg" marginY={8} padding={6} shadow="sm">
        <Flex alignItems="center" gap={5} mb={6}>
          <Avatar name={user?.displayName ?? undefined} size="xl" src={photoUrl ?? undefined} />
          <Text as="h3" fontSize="xl" fontWeight="bold">
            {user?.displayName ?? 'Unknown User'}
          </Text>
        </Flex>
        <DetailRow label="Given Name" value={user?.givenName} />
        <DetailRow label="Surname" value={user?.surname} />
        <DetailRow label="Email" value={<EmailValue email={user?.mail} />} />
        <DetailRow label="Object ID" value={<OidValue oid={oid} />} />
        {error && (
          <Text color="red.500" mt={4}>
            Failed to load user details from Entra ID.
          </Text>
        )}
      </Box>
    </Box>
  );
};
